/**
 * Article list (G2, D7, R3): BulletList → ul > li in TEMPLATE articles only (SectionRenderer renders it
 * when the article render context carries a presentation — Q3 option B); the style block draws the disc
 * markers and the hanging indent (listRules). A legacy article keeps today's OptionsList, so a list that
 * types its own "•" never shows a second marker (Review Focus 1). No handlers: no pointer, no hover.
 */
import React, { memo } from 'react';
import type { CSSProperties } from 'react';
import type { OptionsList as OptionsListType } from '@segmently/schema-models/v2';
import { Text } from '@modules/screen-view-v2/components/shared/Text';
import type { TemplateVariables } from '@modules/screen-view-v2/utils/contentResolver';
import { mapOptionsListStyleToCSS } from '@modules/screen-view-v2/utils/styleMapper';

export interface ArticleListProps {
  options: OptionsListType | undefined;
  locale: string;
  variables?: TemplateVariables;
  editPath?: string;
}

/**
 * A visually neutral article list (no markers; the flex column and gap of today's browse list) for an
 * article list that has no style block to draw it — ArticleTakeaways in an article without tokens (Task 8).
 */
export const NEUTRAL_LIST: CSSProperties = { listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 'var(--sv2-spacing-sm)', width: '100%' };

export const ArticleList = memo(function ArticleList({ options, locale, variables, editPath }: ArticleListProps): React.ReactElement | null {
  const items = options?.items ?? [];
  if (items.length === 0) return null;
  const gap = options?.appearance?.itemsSpacing;
  const literalItemStyle = mapOptionsListStyleToCSS(options?.appearance);
  return (
    <ul className="ap-block ap-list" data-article-list="" data-edit-path={editPath}>
      {items.map((item, index) => {
        const itemStyle: CSSProperties = { ...literalItemStyle, ...(index > 0 && gap !== undefined ? { marginTop: gap } : {}) };
        return (
          <li key={item.variableValue ?? `item-${index}`} style={Object.keys(itemStyle).length > 0 ? itemStyle : undefined}>
            <Text label={item.title} locale={locale} variables={variables} />
          </li>
        );
      })}
    </ul>
  );
});

ArticleList.displayName = 'ArticleList';
